import { useCallback, useRef, useState } from 'react';

/**
 * Lightweight, non-blocking toast — the on-brand replacement for native alert().
 * Lifts the inline savedToast pattern from ReverseSearchScreen into a reusable
 * hook so every screen confirms success / surfaces errors the same way.
 *
 * Usage:
 *   const { showError, showSuccess, toastNode } = useToast();
 *   ...catch (e) { showError(e instanceof Error ? e.message : 'Failed'); }
 *   return (<>{toastNode}<div>…</div></>);
 */
type ToastKind = 'success' | 'error';
type ToastState = { id: number; kind: ToastKind; message: string } | null;

export function useToast() {
  const [toast, setToast] = useState<ToastState>(null);
  const timer = useRef<number | null>(null);

  const show = useCallback((kind: ToastKind, message: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    setToast({ id: Date.now(), kind, message });
    timer.current = window.setTimeout(() => setToast(null), kind === 'error' ? 6000 : 3000);
  }, []);

  const showError = useCallback((message: string) => show('error', message), [show]);
  const showSuccess = useCallback((message: string) => show('success', message), [show]);
  const clearToast = useCallback(() => setToast(null), []);

  const toastNode = toast ? (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      className={`fixed bottom-6 left-1/2 z-[60] flex max-w-[90vw] -translate-x-1/2 items-center gap-3 rounded-lg border px-4 py-2.5 text-sm shadow-lg shadow-black/40 ${
        toast.kind === 'error'
          ? 'border-red-500/45 bg-surface text-red-300'
          : 'border-green-500/45 bg-surface text-green-300'
      }`}
    >
      <span className="leading-snug">{toast.message}</span>
      <button
        type="button"
        onClick={clearToast}
        aria-label="Dismiss notification"
        className="shrink-0 text-xs opacity-70 hover:opacity-100"
      >
        dismiss
      </button>
    </div>
  ) : null;

  return { showError, showSuccess, clearToast, toastNode };
}
